import React, { useEffect, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Image from 'next/legacy/image';
import { useTranslation } from 'i18n';

interface SearchHeaderFieldProps {
  inputValue: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

interface StyleIprops {
  language: string;
  fullWidth: boolean;
}

const useTextFieldStyles = makeStyles<StyleIprops>()((theme, params) => ({
  root: {
    '&&&': {
      height: 32,
      width: params?.fullWidth ? '100%' : 375,
      '@media (max-width: 1185px)': {
        width: 288,
      },
      '@media (max-width: 1098px)': {
        width: 175,
      },
      ...(params?.language === 'fr' && {
        '@media (max-width: 1254px)': {
          width: 175,
        },
        '@media (max-width: 1154px)': {
          width: 100,
        },
      }),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      overflow: 'hidden',
      borderRadius: 100,
      paddingRight: 30,
      fontSize: 13,
      fontWeight: 500,
    },
    input: {
      padding: '4px 0 5px',
    },
  },
  notchedOutline: {
    borderWidth: '1.5px',
    borderColor: `${theme.palette.primary.main} !important`,
  },
}));

const SearchHeaderField: React.FC<React.PropsWithChildren<SearchHeaderFieldProps>> = (props) => {
  const { fullWidth, disabled, inputValue, onChange, placeholder } = props;
  const [t, i18n] = useTranslation();
  const { language } = i18n;
  const { classes: textFieldClasses } = useTextFieldStyles({ language, fullWidth });
  const [searchTerm, setSearchTerm] = useState(inputValue);
  const [inputTimeout, setInputTimeout] = useState(null);

  useEffect(() => () => clearTimeout(inputTimeout as NodeJS.Timeout), [inputTimeout]);
  useEffect(() => setSearchTerm(inputValue), [inputValue]);

  const handleSetInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    const { value } = event.target;
    setSearchTerm(value);
    if (inputTimeout) clearTimeout(inputTimeout as NodeJS.Timeout);
    setInputTimeout(
      setTimeout(() => {
        onChange(event);
      }, 500),
    );
  };

  return (
    <TextField
      fullWidth
      name="search"
      variant="outlined"
      placeholder={placeholder || t<string>('public:searchHeader.placeholder')}
      value={searchTerm}
      onChange={handleSetInputValue}
      disabled={disabled}
      InputProps={{
        classes: textFieldClasses,
        startAdornment: (
          <InputAdornment position="start">
            <Image src="/static/img/icon-search.png" alt="icon-search" width={17} height={17} />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default SearchHeaderField;
